import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

export interface HealthService {
  name: string;
  status: 'up' | 'down';
  message?: string;
}

interface HealthState {
  services: HealthService[];
  overallStatus: 'ok' | 'error';
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: HealthState = {
  services: [],
  overallStatus: 'ok',
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getExternalServicesHealth = createAsyncThunk(
  'health/getExternalServicesHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/health/external-services');
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| SLICE ||============================== //

const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get external services health
      .addCase(getExternalServicesHealth.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getExternalServicesHealth.fulfilled, (state, action) => {
        if (!action.payload) {
          state.services = [];
          state.overallStatus = 'error';
          state.status = 'success';
          state.error = '';
          return;
        }

        // Extract the actual data - handle both wrapped and unwrapped responses
        const responseData = action.payload.response || action.payload;
        const details = responseData.details || {};

        // Convert health check response to service array
        const services: HealthService[] = [];

        if (details.jira) {
          services.push({
            name: 'Jira',
            status: details.jira.status === 'up' ? 'up' : 'down',
            message: details.jira.message,
          });
        }

        if (details.github) {
          services.push({
            name: 'GitHub',
            status: details.github.status === 'up' ? 'up' : 'down',
            message: details.github.message,
          });
        }

        if (details.gitlab) {
          services.push({
            name: 'GitLab',
            status: details.gitlab.status === 'up' ? 'up' : 'down',
            message: details.gitlab.message,
          });
        }

        if (details.claude) {
          services.push({
            name: 'Claude AI',
            status: details.claude.status === 'up' ? 'up' : 'down',
            message: details.claude.message,
          });
        }

        state.services = services;
        state.overallStatus = responseData.status === 'ok' ? 'ok' : 'error';
        state.status = 'success';
        state.error = '';
      })
      .addCase(getExternalServicesHealth.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        state.services = [];
        state.overallStatus = 'error';
      });
  },
});

// ==============================|| SELECTORS ||============================== //

export const selectHealthServices = (state: { health: HealthState }) => state.health.services;
export const selectHealthOverallStatus = (state: { health: HealthState }) => state.health.overallStatus;
export const selectHealthStatus = (state: { health: HealthState }) => state.health.status;
export const selectHealthError = (state: { health: HealthState }) => state.health.error;

export default healthSlice.reducer;
